import { useState, useEffect } from 'preact/hooks';
import type { Connection } from '../../../types';
import { connectionManagementService } from '../../../services/connections/connection-management.service';
import { connectorRegistryService } from '../../../services/connectors/connector-registry.service';
import { agentsService } from '../../../services/agents.service';
import { useScopeContext } from '../../../contexts/ScopeContext';
import { toast } from '../../../services/toast.service';

interface PermissionsTabProps {
  connection: Connection;
  onConnectionUpdate: () => void;
}

export function PermissionsTab({ connection, onConnectionUpdate }: PermissionsTabProps) {
  const scope = useScopeContext();
  const [agents, setAgents] = useState<any[]>([]);
  const [selectedAgentIds, setSelectedAgentIds] = useState<string[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [toolPermissions, setToolPermissions] = useState<Record<string, 'allowed' | 'requires-approval' | 'denied'>>({});
  const [savingPermissions, setSavingPermissions] = useState(false);

  useEffect(() => {
    if (connection?.active && connection.connectorId) {
      loadAgents();
      loadTools();
      // Load agent access from connection's agentAccess object
      if (connection.agentAccess) {
        const enabledAgentIds = Object.keys(connection.agentAccess)
          .filter(agentId => connection.agentAccess![agentId]?.enabled);
        setSelectedAgentIds(enabledAgentIds);
      }
    }
  }, [connection?.active, connection.connectionId || connection.id]);

  const loadAgents = async () => {
    try {
      const fetchedAgents = await agentsService.getAgents(scope.teamId);
      setAgents(fetchedAgents);
    } catch (err) {
      console.error('Failed to load agents:', err);
    }
  };

  const loadTools = async () => {
    try {
      const fetchedTools = await connectorRegistryService.getTools(scope.teamId, connection.connectorId!);
      setTools(fetchedTools);

      // Initialize permissions for ALL current tools, using existing values where available
      // This ensures new tools get explicit defaults rather than being omitted from the payload
      const defaultPermissions: Record<string, 'allowed' | 'requires-approval' | 'denied'> = {};
      fetchedTools.forEach((tool: any) => {
        // Use existing permission if available, otherwise default to 'requires-approval'
        defaultPermissions[tool.name] =
          (connection.toolPermissions?.[tool.name] as 'allowed' | 'requires-approval' | 'denied') ||
          'requires-approval';
      });
      setToolPermissions(defaultPermissions);
    } catch (err) {
      console.error('Failed to load tools:', err);
    }
  };

  const handleSavePermissions = async () => {
    try {
      setSavingPermissions(true);

      // Update permissions
      await connectionManagementService.updatePermissions(scope.teamId, connection.connectionId || connection.id, {
        agentIds: selectedAgentIds,
        toolPermissions
      });

      toast.success('Permissions saved successfully');
      await onConnectionUpdate();
    } catch (err) {
      toast.error(`Failed to save permissions: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSavingPermissions(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Tool Permissions Section */}
      {tools.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Tool Permissions</h3>
          <div className="alert mb-4">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Configure permissions for individual tools. These settings control how agents can interact with each tool.</span>
          </div>

          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Tool</th>
                  <th>Description</th>
                  <th>Permission</th>
                </tr>
              </thead>
              <tbody>
                {tools.map((tool: any) => (
                  <tr key={tool.name}>
                    <td className="font-medium">{tool.name}</td>
                    <td className="text-sm opacity-70">{tool.description || 'No description'}</td>
                    <td>
                      <select
                        className="select select-bordered select-sm"
                        value={toolPermissions[tool.name] || 'requires-approval'}
                        onChange={(e) => {
                          const value = (e.target as HTMLSelectElement).value as 'allowed' | 'requires-approval' | 'denied';
                          setToolPermissions({ ...toolPermissions, [tool.name]: value });
                        }}
                      >
                        <option value="allowed">✅ Allow</option>
                        <option value="requires-approval">⚠️ Requires Approval</option>
                        <option value="denied">❌ Deny</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Agent Access Section */}
      <div>
        <h3 className="text-lg font-semibold mb-3">Agent Access</h3>
        <div className="alert mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
          <span>Select which AI agents can use this connection.</span>
        </div>

        {agents && agents.length > 0 ? (
          <div className="space-y-4">
            {agents.map((agent: any) => {
              const isSelected = selectedAgentIds.includes(agent.id);

              return (
                <div key={agent.id} className="card bg-base-200">
                  <div className="card-body">
                    <label className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        className="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          if (e.currentTarget.checked) {
                            setSelectedAgentIds([...selectedAgentIds, agent.id]);
                          } else {
                            setSelectedAgentIds(selectedAgentIds.filter(id => id !== agent.id));
                          }
                        }}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{agent.name}</div>
                        {agent.description && (
                          <div className="text-sm opacity-70">{agent.description}</div>
                        )}
                      </div>
                    </label>

                    {/* Show tool checkboxes (disabled) */}
                    {isSelected && tools.length > 0 && (
                      <div className="mt-3 ml-7 opacity-50">
                        <div className="text-sm font-medium mb-2">Tool Access:</div>
                        <div className="text-sm italic mb-2">Agent level permissions coming soon</div>
                        <div className="grid grid-cols-2 gap-2">
                          {tools.map(tool => {
                            // Only show tools that have 'allowed' permission
                            if (toolPermissions[tool.name] === 'allowed') {
                              return (
                                <label key={tool.name} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm"
                                    checked={true}
                                    disabled={true}
                                  />
                                  <span className="text-sm">{tool.name}</span>
                                </label>
                              );
                            }
                            return null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="alert alert-info">
            <span>No agents available or loading...</span>
          </div>
        )}
      </div>

      {/* Save Button - positioned at the bottom right */}
      <div className="flex justify-end mt-6">
        <button
          className="btn btn-primary"
          onClick={handleSavePermissions}
          disabled={savingPermissions}
        >
          {savingPermissions ? 'Saving...' : 'Save Permissions'}
        </button>
      </div>
    </div>
  );
}
